/**
 * @deprecated Use RTK Query via baseApi.injectEndpoints() instead.
 * 
 * API Client (Legacy) — Used by KVS Samagam, Attendance, Auth modules
 * 
 * SECURITY:
 * - 401 Unauthorized → auto logout + redirect to landing page
 * - credentials: include → cookies sent with every request
 * 
 * NOTE: New modules should use RTK Query via baseApi.injectEndpoints() instead.
 * This client is maintained for backward compatibility with existing modules.
 * See PM-SHRI and Dashboard modules for RTK Query examples.
 */

import axios from 'axios';
import { freshReplayHeaders } from './replayHeaders';
import { clientBindingHeaders } from './clientBinding';

// API Base URL - configured via REACT_APP_API_URL in .env file
export const BASE_URL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

/**
 * Sent on every request to this backend. Auth tokens ride in httpOnly cookies;
 * the client binding (security audit #14) rides here — see clientBinding.ts.
 */
function getAuthHeaders(): Record<string, string> {
  return clientBindingHeaders();
}

/**
 * Endpoints where a 401 means "these credentials are wrong", NOT "your session
 * expired". Logging out and redirecting on these would replace the real error
 * (e.g. "Invalid credentials") with a bogus session-expiry message and bounce
 * the user off the very page they are trying to use.
 */
const CREDENTIAL_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  // Public flow — the caller is logged out by definition, so a redirect here
  // would just bounce them off the page they need.
  '/auth/forgot-password/',
  // Called by the login form to confirm the session it was just given
  // (security audit #14). A 401 means that session does not work from this
  // browser: the login failed, and the form shows the server's reason.
  '/auth/session',
];

// NOTE: /auth/change-password/* is deliberately NOT in this list. Its handlers
// return 400 for a wrong current password or a stale OTP token, so a 401 from
// those routes really does mean the session died — and the normal logout +
// redirect is the right response.

function isCredentialEndpoint(path: string): boolean {
  return CREDENTIAL_ENDPOINTS.some((p) => path.startsWith(p));
}

/**
 * Routes the backend guards with replay protection. Every JSON answer from
 * them carries `requestId`, the nonce of the request it answers (security
 * audit #14, response replay). A response captured earlier and pasted over the
 * real one carries some other request's nonce and is refused here. On these
 * routes a missing echo is refused too: otherwise deleting the field would be
 * enough to get past the check.
 */
const ECHOING_ROUTES = [
  '/auth/login',
  '/auth/register',
  '/auth/session',
  '/auth/refresh-token',
  '/auth/forgot-password/',
  '/auth/change-password/',
];

function answersThisRequest(path: string, nonce: string, body: any): boolean {
  const echoed = body !== null && typeof body === 'object' ? body.requestId : undefined;
  if (echoed === undefined) {
    return !ECHOING_ROUTES.some((p) => path.startsWith(p));
  }
  return echoed === nonce;
}

/**
 * Handle 401 Unauthorized — clear session and redirect to login
 * Shared by both apiGet (axios) and apiPost (fetch)
 */
function handleUnauthorized(): void {
  console.warn('[Auth] 401 Unauthorized — session expired. Logging out...');

  // Clear all auth data
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('captchaId');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userRoleCategory');
  localStorage.removeItem('userRoleId');
  localStorage.removeItem('username');
  localStorage.removeItem('userName');
  localStorage.removeItem('userEmail');

  // Clear cached data
  sessionStorage.clear();

  // Redirect to landing page for re-login
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
}

/**
 * Error carrying the server's HTTP status and structured fields so callers can
 * react to specific failures (e.g. 429 rate limit) instead of showing a generic
 * "something failed" message.
 */
export class ApiError extends Error {
  status: number;
  /** Seconds to wait before retrying — set by the backend on 429 responses. */
  retryAfter?: number;
  code?: string;

  constructor(message: string, status: number, retryAfter?: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.retryAfter = retryAfter;
    this.code = code;
  }
}

/**
 * The machine-readable code the backend's error handler puts on a response.
 *
 * It arrives as `error` (the human sentence is `message`), so reading only
 * `code`/`error_code` left ApiError.code permanently undefined. `error` is
 * accepted only when it looks like a code — SCREAMING_SNAKE — because a few
 * handlers put a plain sentence there instead.
 */
function errorCodeFrom(body: any): string | undefined {
  if (body?.error_code) return body.error_code;
  if (body?.code) return body.code;
  return typeof body?.error === 'string' && /^[A-Z][A-Z0-9_]*$/.test(body.error)
    ? body.error
    : undefined;
}

/** Build an ApiError from a parsed error body, falling back to a sane message. */
function toApiError(body: any, status: number, fallback: string): ApiError {
  const message = body?.message || body?.error || fallback;
  // The rate limiter puts retryAfter at the top level; handlers that throw a
  // ServiceError carry it in `details`, which is where the login lockout and
  // per-account throttle report how long the caller must wait.
  const retryAfter =
    typeof body?.retryAfter === 'number'
      ? body.retryAfter
      : typeof body?.details?.retryAfter === 'number'
        ? body.details.retryAfter
        : undefined;
  return new ApiError(message, status, retryAfter, errorCodeFrom(body));
}

// Create axios instance with interceptor
const axiosInstance = axios.create({
  withCredentials: true,
});

// Response interceptor — handle 401 globally for all axios requests
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

export async function apiGet(path: string) {
  try {
    const response = await axiosInstance.get(BASE_URL + path, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    // Surface the server's own message/status (e.g. 429 rate limit) instead of
    // axios's opaque "Request failed with status code NNN".
    if (error?.response) {
      throw toApiError(error.response.data, error.response.status, `Request failed (${error.response.status})`);
    }
    throw new ApiError('Network error. Please check your connection.', 0);
  }
}

export async function apiPost(
  path: string,
  data: any,
  options?: {
    /**
     * Stable key for a retryable sensitive transaction (password reset/change).
     * Reuse the SAME key when retrying the SAME operation — the server then
     * replays the first response instead of applying the change twice. Do NOT
     * reuse a key for a different payload; the server refuses that with 422.
     */
    idempotencyKey?: string;
  }
) {
  // Replay protection (security audit #14): fresh nonce + timestamp per
  // attempt. Harmless on unguarded routes, required on the auth routes once the
  // backend runs in enforce mode. Kept so the answer can be matched to it below.
  const replayHeaders = freshReplayHeaders();
  const response = await fetch(BASE_URL + path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...replayHeaders,
      ...(options?.idempotencyKey ? { 'Idempotency-Key': options.idempotencyKey } : {}),
      ...getAuthHeaders(),
    },
    body: JSON.stringify(data),
    credentials: 'include',
  });

  // Handle 401 for fetch-based POST requests — but never on the credential
  // endpoints, where 401 just means the password/session supplied was rejected
  // and the caller wants to show that message in place.
  if (response.status === 401 && !isCredentialEndpoint(path)) {
    handleUnauthorized();
    throw new Error('Unauthorized — session expired');
  }

  // A rate-limited or proxy-level failure may not return JSON at all
  let responseData: any = null;
  try {
    responseData = await response.json();
  } catch {
    responseData = null;
  }

  if (!response.ok) {
    throw toApiError(responseData, response.status, `Request failed (${response.status})`);
  }

  // Response replay (security audit #14): only act on a success that answers
  // THIS request. See ECHOING_ROUTES.
  if (!answersThisRequest(path, replayHeaders['X-Request-Id'], responseData)) {
    throw new ApiError(
      'The server response could not be verified. Please try again.',
      response.status,
      undefined,
      'RESPONSE_NOT_VERIFIED'
    );
  }

  return responseData;
}
